import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Compass, Loader2, Mail, Lock, User } from 'lucide-react';
import { PlanInfoCards } from '../components/PlanInfoCards';
import { ReferralBanner } from '../components/ReferralBanner';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!privacyAccepted) {
      toast.error('Debes aceptar el Aviso de Privacidad para continuar');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const referralCode = localStorage.getItem('klarvi_referral_code');
      await register(name, email, password, referralCode || undefined);
      localStorage.removeItem('klarvi_referral_code');
      toast.success('¡Cuenta creada exitosamente!');
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', { method: 'email' });
        window.gtag('event', 'conversion', {
          send_to: 'AW-18303994422/_iJ2COH4quscELaUg5hE',
          value: 1,
          currency: 'MXN',
        });
      }
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.message || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Compass className="w-8 h-8 text-[#2563EB]" />
          <span className="font-bold text-2xl tracking-tight font-['Manrope']">
            Klarvi
          </span>
        </Link>

        <Card className="border shadow-lg animate-scale-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-['Manrope']">Crear Cuenta</CardTitle>
            <CardDescription>
              Comienza a tomar mejores decisiones laborales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre Completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              {/* Privacy Notice Checkbox */}
              <div className="space-y-2 pt-1">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privacy-checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB] cursor-pointer flex-shrink-0"
                    data-testid="privacy-checkbox"
                  />
                  <label htmlFor="privacy-checkbox" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    He leído y acepto el{' '}
                    <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] hover:underline font-medium">
                      Aviso de Privacidad
                    </a>
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground/80 leading-relaxed pl-7">
                  Klarvi utiliza tus datos personales (nombre, correo, e información laboral que ingreses) únicamente para brindarte el servicio y mejorar tu experiencia. No los compartimos con terceros sin tu consentimiento. Puedes ejercer tus derechos de acceso, rectificación, cancelación u oposición (ARCO) escribiendo a{' '}
                  <a href="mailto:contacto@klarvi.org" className="text-[#2563EB] hover:underline">contacto@klarvi.org</a>.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0F172A] hover:bg-[#0F172A]/90"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Crear Cuenta
              </Button>
            </form>

            <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
                o continúa con
              </span>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={loginWithGoogle}
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </Button>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{' '}
              <Link
                to="/login"
                className="text-[#2563EB] hover:underline font-medium"
                data-testid="login-link"
              >
                Iniciar sesión
              </Link>
            </p>
          </CardContent>
        </Card>
        <PlanInfoCards />
        <ReferralBanner />
      </div>
    </div>
  );
}
